import axios, { AxiosError } from 'axios';

// Types
import {
  POSTCreateLOB,
  POSTCreateHSA,
  PATCHHSAConfig,
  POSTCreateClient,
  POSTCreateCustomer,
  CreateCustomerBody,
  POSTProcessHSAFile,
  GETHSAFileSignedURL,
  CreateAcctConfigBody,
  POSTCreateAcctConfig,
  AddHSASignifiersBody,
  POSTProcessHSAFileBody,
  HSAFileValidationDetails,
  UpdateHSAPrelimDatesBody,
  GETHSAConfiguration,
  POSTErrorRes,
} from 'types';

export const getOnboardingBaseData = async () => {
  const { data } = await axios.get<GETBaseOnboardingData>(
    '/get_available_data_base_set_up/'
  );
  return data;
};

export const fetchAvailableData = async () => {
  const response = await fetch('/get_available_data_base_set_up/');
  const data: { agencies: Agency[] } = await response.json();
  return data.agencies;
};

export const addNewClient = async (agency_id: number, client_name: string) => {
  const { data } = await axios.post<POSTCreateClient>('/add-new-client/', {
    agency_id,
    client_name,
  });
  return data;
};

export const addNewLOB = async (
  client_id: number,
  line_of_business: string
) => {
  const { data } = await axios.post<POSTCreateLOB>('/create-lob/', {
    client_id,
    line_of_business,
  });
  return data;
};

export const createCustomerBQ = async (customer_id: string | number) => {
  const { data } = await axios.post(`/create-new-bq-dataset/${customer_id}/`);
  return data;
};

export const createCustomer = async (body: CreateCustomerBody) => {
  const { data } = await axios.post<POSTCreateCustomer>(
    '/create-customer-information/',
    body
  );
  await createCustomerBQ(data.data.customer_id);
  return data;
};

export const createHSAConfig = async (customer_id: string | number) => {
  const { data } = await axios.post<POSTCreateHSA>(
    `/onboard-hsa-config/${customer_id}/`
  );
  return data;
};

export const createAcctConfig = async (
  customer_id: string | number,
  body: CreateAcctConfigBody
) => {
  const { data } = await axios.post<POSTCreateAcctConfig>(
    `/create-account-cfg/${customer_id}/`,
    body
  );
  return data;
};

export const addBrandSignifiers = async (
  customer_id: string | number,
  hsa_id: string | number,
  body: AddHSASignifiersBody
) => {
  const { data } = await axios.patch<PATCHHSAConfig>(
    `/add-brand-signifiers/${customer_id}/${hsa_id}`,
    body
  );
  return data;
};

export const updatePrelimDates = async (
  customer_id: string | number,
  hsa_id: string | number,
  body: UpdateHSAPrelimDatesBody
) => {
  const { data } = await axios.patch<PATCHHSAConfig>(
    `/update-hsa-prelim-date/${customer_id}/${hsa_id}/`,
    body
  );
  return data;
};

export const updateWhitelistedDomains = async (
  customer_id: string | number,
  hsa_id: string | number,
  whitelisted_domains: string[]
) => {
  const { data } = await axios.patch<PATCHHSAConfig>(
    `/update-hsa-whitelisted-domains/${customer_id}/${hsa_id}/`,
    {
      whitelisted_domains,
    }
  );
  return data;
};

export const getHSAFileSignedURL = async (
  customerID: string | number,
  hsaID: string | number,
  filename: string
) => {
  const { data } = await axios.get<GETHSAFileSignedURL>(
    `/get-file-signed-upload-url/${customerID}/${hsaID}`,
    {
      params: {
        filename,
        file_type: 'text/csv',
      },
    }
  );
  return data.data;
};

export const processHSAFile = async (
  customerID: string | number,
  hsaID: string | number,
  bodyParams: POSTProcessHSAFileBody
) => {
  const body = new FormData();
  for (const [key, value] of Object.entries(bodyParams)) {
    body.append(key, value);
  }

  const { data } = await axios.post<POSTProcessHSAFile>(
    `/initiate-hsa-processing/${customerID}/${hsaID}`,
    body
  );
  return data;
};

export const uploadHSAFileToGCS = async (signedURL: string, file: File) => {
  const res = await fetch(signedURL, {
    method: 'PUT',
    headers: {
      Accept: 'application/json, text/plain',
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!res.ok) {
    throw new Error('File upload failed.');
  }

  return { status: 'success' };
};

export const submitHSAFileUpload = async (
  customerID: string | number,
  hsaID: string | number,
  file: File,
  hsaFileInfo: HSAFileValidationDetails
) => {
  try {
    const signedURL = await getHSAFileSignedURL(customerID, hsaID, file.name);
    if (!signedURL.signed_url) {
      throw new Error('Unable to obtain signed URL');
    }

    await uploadHSAFileToGCS(signedURL.signed_url, file);

    const processFileRes = await processHSAFile(customerID, hsaID, {
      ...hsaFileInfo,
      gcs_path: signedURL.gcs_file_path,
    });

    return processFileRes;
  } catch (err) {
    if (axios.isAxiosError<POSTErrorRes>(err)) {
      throw new Error(
        `HSA processing failed (${err.response?.status}): ${
          err.response?.data?.error?.message ?? err.message
        }`
      );
    }

    if (err instanceof Error) {
      throw new Error(`HSA upload failed: ${err.message}`);
    }

    throw new Error('HSA upload failed due to an unknown error');
  }
};

export const getHsaConfigLists = async (
  customerId: string
): Promise<GETHSAConfiguration | null> => {
  try {
    const { data } = await axios.get<GETHSAConfiguration>(
      `/get-hsa-config-list/${customerId}`
    );
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 404) {
      // No HSA data for this customer — not really an error
      return null;
    }
    // Re-throw other errors
    throw axiosError;
  }
};
